import { RequiredMark } from '@/components/ui';
import {
  COLLECTION_FIELD_TYPE_LABELS,
  isChoiceType,
  parseOptions,
  type EventCollectionField,
} from '../types/collectionField';

interface CollectionFieldPreviewProps {
  fields: ReadonlyArray<EventCollectionField>;
}

/** 필드 유형별 placeholder. */
const PLACEHOLDER: Record<string, string> = {
  TEXT: '입력하세요',
  PHONE: '010-0000-0000',
  DATE: 'YYYY-MM-DD',
  EMAIL: 'example@everline.co.kr',
  ACCOUNT: '은행 / 계좌번호 / 예금주',
};

const INPUT_STYLE = {
  background: 'var(--surface-1)',
  borderColor: 'var(--divider-strong)',
  color: 'var(--content-muted)',
} as const;

/**
 * 단일 필드의 입력 컨트롤 미리보기. 선택형(RADIO/SELECT)은 보기 목록을 렌더하고,
 * 자유 입력형은 유형별 input 을 렌더한다(모두 disabled — 미리보기 전용).
 * 선택형인데 보기가 비면 안내 문구를 보여준다(BO 가 저장을 막지만 편집 중 상태 가시화).
 */
const FieldControl = ({ field }: { field: EventCollectionField }) => {
  if (isChoiceType(field.fieldType)) {
    const options = parseOptions(field.optionsJson);
    if (options.length === 0) {
      return (
        <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          보기 목록을 추가하세요.
        </span>
      );
    }
    if (field.fieldType === 'SELECT') {
      return (
        <select
          disabled
          className="h-9 rounded-md border px-3 text-[13px]"
          style={INPUT_STYLE}
          aria-label={field.label}
        >
          <option>선택</option>
          {options.map((opt, i) => (
            <option key={i}>{opt}</option>
          ))}
        </select>
      );
    }
    // RADIO
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-1" role="radiogroup" aria-label={field.label}>
        {options.map((opt, i) => (
          <label key={i} className="flex items-center gap-1.5 text-[13px]" style={{ color: 'var(--content-muted)' }}>
            <input type="radio" name={field.fieldKey} disabled aria-hidden="true" />
            <span>{opt}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <input
      type={field.fieldType === 'DATE' ? 'date' : field.fieldType === 'EMAIL' ? 'email' : 'text'}
      disabled
      placeholder={PLACEHOLDER[field.fieldType] ?? '입력하세요'}
      className="h-9 rounded-md border px-3 text-[13px]"
      style={INPUT_STYLE}
      aria-label={field.label}
    />
  );
};

/**
 * 응모폼 미리보기 — 스토어프론트 결제하기 옵션 모달에 렌더될 폼을 그대로 보여준다.
 * collect=true 필드만, sortOrder 순으로. 민감필드는 동의 안내를 함께 표시한다.
 */
export const CollectionFieldPreview = ({ fields }: CollectionFieldPreviewProps) => {
  const visible = fields
    .filter((f) => f.collect)
    .slice()
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const hasSensitive = visible.some((f) => f.sensitive);

  return (
    <div
      className="flex flex-col gap-3 rounded-lg p-4"
      style={{ background: 'var(--surface-0)', border: '1px solid var(--divider-strong)' }}
      data-testid="collection-field-preview"
    >
      <p className="text-[13px] font-semibold" style={{ color: 'var(--content-strong)' }}>
        응모 정보 입력
      </p>

      {visible.length === 0 ? (
        <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
          수집할 항목이 없습니다. 항목을 추가하고 "수집"을 켜면 여기에 미리보기가 표시됩니다.
        </p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {visible.map((field) => (
            <label key={field.fieldId} className="flex flex-col gap-1">
              <span className="text-[12px] font-medium" style={{ color: 'var(--content-default)' }}>
                {field.label || COLLECTION_FIELD_TYPE_LABELS[field.fieldType]}
                {field.required ? <RequiredMark /> : null}
                {field.sensitive ? (
                  <span className="ml-1 text-[10px]" style={{ color: 'var(--content-muted)' }}>
                    (민감)
                  </span>
                ) : null}
              </span>
              <FieldControl field={field} />
            </label>
          ))}

          {hasSensitive ? (
            <label className="mt-1 flex items-start gap-2 text-[12px]" style={{ color: 'var(--content-muted)' }}>
              <input type="checkbox" disabled className="mt-0.5" aria-hidden="true" />
              <span>
                개인정보 수집·이용에 동의합니다. (필수) — 환불계좌/생년월일 등 민감정보는 암호화되어
                저장됩니다.
              </span>
            </label>
          ) : null}
        </div>
      )}
    </div>
  );
};
